import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  formatMediaLimitsIndication,
  MediaLimitsIndicationView,
} from '@app/modules/social-export/lib/format-media-limits-indication';
import { resolveMediaLimitsIndication } from '@app/modules/social-export/lib/resolve-media-limits-indication';
import { SocialExportGetStationConnectionStateDto } from '@mi-front/api-v2';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

/**
 * Resolves the localized media-limits indication for one station connection and clip duration.
 *
 * Use this when the publish UI already has a concrete connection (e.g. upload wizard after
 * account selection). Soft tips and blocked states are shown as a page-level MediaLimitsAlert.
 *
 * Evaluation follows the social-export API contract on `connection.mediaLimits.default`:
 * pick the best-fit quality tier for `durationMs`, then branch on that tier's `delivery`
 * (`as_source` / `platform_displays_lower` / `needs_transcoding`, or blocked if none fit).
 *
 * @param connection - Station connection whose `mediaLimits` to evaluate; undefined yields no indication.
 * @param durationMs - Clip length in milliseconds. When undefined, only soft tips from the
 *   station-height tier can appear (no block / downscale without a known duration).
 * @returns View-model with `kind`, localized `message`, and `blocksPublish`, or undefined when ok / no limits.
 */
export function useMediaLimitsIndication(
  connection: SocialExportGetStationConnectionStateDto | undefined,
  durationMs: number | undefined,
): MediaLimitsIndicationView | undefined {
  const { $t } = useIntl();

  return useMemo(() => {
    // API tiers express budgets in seconds; UI callers typically have ms (fragment chunks).
    const durationSec = durationMs != null ? durationMs / 1000 : undefined;
    const indication = resolveMediaLimitsIndication(connection?.mediaLimits, durationSec);
    const platformName = connection ? socialPlatforms.get(connection.platform).name : '';
    return formatMediaLimitsIndication(indication, $t, platformName);
  }, [$t, connection, durationMs]);
}
